import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { buildStatusDeltaMap } from '@/features/metrics/components/statusMetricCards';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { collapseExecutionStatusCounts } from '@/features/shared/utils/projectStatusPresentation';
import type { EntityTrendsPageConfig, EntityStatusKey } from './useEntityTrendsPage.types';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';

type UseEntityTrendsSummaryInput<TEntityItem, TSummary extends { total: number }> = {
  config: EntityTrendsPageConfig<TEntityItem, TSummary>;
  isLatestMode: boolean;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  isAuthenticated: boolean;
  summaryInput: GridQueryInput;
  previousSummaryInput: GridQueryInput | null;
  summaryFiltersKey: string;
  previousSummaryFiltersKey: string;
  showDelta: boolean;
};

type UseEntityTrendsSummaryResult<TSummary extends { total: number }> = {
  summary: TSummary | null;
  previousSummary: TSummary | null;
  counts: Partial<Record<EntityStatusKey, number>>;
  previousCounts: Partial<Record<EntityStatusKey, number>>;
  deltas: ReturnType<typeof buildStatusDeltaMap> | undefined;
  metricCardsLoading: boolean;
  summaryIsFetching: boolean;
  summaryIsLoading: boolean;
};

export const useEntityTrendsSummary = <TEntityItem, TSummary extends { total: number }>({
  config,
  isLatestMode,
  selectedProjectId,
  selectedOrganisationId,
  isAuthenticated,
  summaryInput,
  previousSummaryInput,
  summaryFiltersKey,
  previousSummaryFiltersKey,
  showDelta,
}: UseEntityTrendsSummaryInput<TEntityItem, TSummary>): UseEntityTrendsSummaryResult<TSummary> => {
  const { groupErroredWithFailed } = useProjectStatusPresentation();
  const summaryQuery = useQuery<TSummary>({
    queryKey: isLatestMode
      ? config.summaryKey(
          selectedProjectId ?? null,
          selectedOrganisationId ?? null,
          summaryFiltersKey,
        )
      : config.executionSummaryKey(
          selectedProjectId ?? null,
          selectedOrganisationId ?? null,
          summaryFiltersKey,
        ),
    enabled: isAuthenticated,
    queryFn: async () =>
      isLatestMode
        ? config.queryLatestSummary(
            selectedProjectId,
            selectedOrganisationId ?? undefined,
            summaryInput,
          )
        : config.queryExecutionSummary(
            selectedProjectId,
            selectedOrganisationId ?? undefined,
            summaryInput,
          ),
    placeholderData: keepPreviousData,
  });
  const previousSummaryQuery = useQuery<TSummary>({
    queryKey: config.executionSummaryKey(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousSummaryFiltersKey,
    ),
    enabled: isAuthenticated && showDelta && !isLatestMode,
    queryFn: async () =>
      config.queryExecutionSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        previousSummaryInput ?? summaryInput,
      ),
    placeholderData: keepPreviousData,
  });

  const summary = summaryQuery.data ?? null;
  const previousSummary = !isLatestMode ? (previousSummaryQuery.data ?? null) : null;
  const counts = useMemo(
    () => collapseExecutionStatusCounts(config.buildCounts(summary), groupErroredWithFailed),
    [config, groupErroredWithFailed, summary],
  );
  const previousCounts = useMemo(
    () =>
      collapseExecutionStatusCounts(config.buildCounts(previousSummary), groupErroredWithFailed),
    [config, groupErroredWithFailed, previousSummary],
  );
  const deltas = useMemo(
    () => (showDelta && previousSummary ? buildStatusDeltaMap(counts, previousCounts) : undefined),
    [counts, previousCounts, previousSummary, showDelta],
  );
  const currentSummaryPending = summaryQuery.isFetching && !summaryQuery.isError;
  const previousSummaryPending =
    showDelta && !isLatestMode && previousSummaryQuery.isFetching && !previousSummaryQuery.isError;

  return {
    summary,
    previousSummary,
    counts,
    previousCounts,
    deltas,
    metricCardsLoading: currentSummaryPending || previousSummaryPending,
    summaryIsFetching: summaryQuery.isFetching,
    summaryIsLoading: summaryQuery.isLoading,
  };
};
